import React, { createContext, useContext, useEffect, useState } from "react";
import { DeleteCookie, GetCookie } from "../common/CookieHelper";
import { doc, getDoc, getDocFromServer, setDoc, updateDoc, increment, serverTimestamp } from "firebase/firestore";
import { SiteSettings, siteSettingsDefault } from "../model/SiteSettingModel";
import { Cart } from "../model/CartModel";
import { auth, db, getSiteKey, maindb } from "../firebaseConfig";
import { onAuthStateChanged, User, signOut } from "firebase/auth";
import { CartGateway } from "../gateways/CartGateway";
import { Site, SiteCookie } from "../model/SiteModel";
import { Utils } from "../common/Utils";
import { OrderGateway } from "../gateways/OrderGateway";
import { OrderStatus } from "../model/OrderModel";
import { GetCartCookieKey } from "../gateways/SiteGateway";

interface GlobalContextType {
  user: User | null;
  isAdmin: boolean;
  userloaded: boolean;
  settingsLoaded: boolean;
  logOut: () => Promise<void>; // Add signOut function to context
  cart: Cart;
  updateCart: () => void;
  settings: SiteSettings;
  site: Site | undefined;
  scannedDeliveryLocation: string;
  setScannedDeliveryLocation: (val: string) => void;
}

const GlobalContext = createContext<GlobalContextType | undefined>(undefined);

export const GlobalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cart, setCart] = useState<Cart>({ cartItems: [] });
  const [settings, setSettings] = useState<SiteSettings>(siteSettingsDefault);
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [userloaded, setUserLoaded] = useState(false);
  const [site, setSite] = useState<Site>();
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [scannedDeliveryLocation, setScannedDeliveryLocation] = useState("");

  const checkSnapScan = async () => {
    const snapScanCookie = GetCookie(SiteCookie.SnapScanRedirect);
    if (snapScanCookie) {
      const { orderId, email, phoneCode, phoneNumber } = JSON.parse(snapScanCookie);
      const clearCart = async () => {
        DeleteCookie(GetCartCookieKey);
        DeleteCookie(SiteCookie.SnapScanRedirect);
        if (user) {
          await CartGateway.DeleteCart(user);
        }
        setCart({ cartItems: [] });
      };

      if (orderId !== cart.orderId) {
        await clearCart();
        return true;
      }

      const snapScanPaymentResult = await OrderGateway.GetSnapScanOrderSummary(orderId, phoneCode, phoneNumber, email);
      if (snapScanPaymentResult && snapScanPaymentResult.status !== OrderStatus.PaymentPending) {
        await clearCart();
        return true;
      }
    }
    return false;
  };

  const updateCart = async () => {
    if (!user) {
      const savedCart = GetCookie(GetCartCookieKey);

      if (savedCart) {
        const cart = JSON.parse(savedCart) as Cart;

        const snapScan = await checkSnapScan();
        if (!snapScan) {
          setCart(cart);
        }
      } else {
        setCart({ cartItems: [] });
      }
      return;
    }

    const userCartRef = doc(db, "carts", user.uid);
    const cartSnapshot = await getDoc(userCartRef);

    if (cartSnapshot.exists()) {
      const cart = cartSnapshot.data() as Cart;
      const snapScan = await checkSnapScan();
      if (!snapScan) {
        setCart(cart);
      }
    } else {
      setCart({ cartItems: [] });
    }
  };

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const key = getSiteKey();
        if (key) {
          const ref = doc(maindb, "sites", key);
          const siteSnap = await getDoc(ref);
          if (!siteSnap.exists()) {
            return;
          }
          const site = siteSnap.data() as Site;
          setSite(site);
          if (site && !site.siteLoaded) {
            return;
          }
        }

        const storageKey = `orderq-${key}`;
        const data = sessionStorage.getItem(storageKey);
        if (data) {
          const settings = JSON.parse(data) as SiteSettings;
          setSettings(settings);
          return;
        }

        const docRef = doc(db, "website", "settings");
        const docSnap = await getDocFromServer(docRef);
        if (docSnap.exists()) {
          const settings = docSnap.data() as SiteSettings;
          setSettings(settings);

          sessionStorage.setItem(storageKey, JSON.stringify(settings));
        }
      } catch (error) {
        console.error("Error fetching settings:", error);
      } finally {
        setSettingsLoaded(true);
      }
    };

    fetchSettings();

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);

      if (currentUser) {
        const savedCart = GetCookie(GetCartCookieKey);
        if (savedCart) {
          const cart = JSON.parse(savedCart) as Cart;
          if (cart) {
            CartGateway.SaveCart(cart, currentUser);
          }

          DeleteCookie(GetCartCookieKey);
        }
        updateCart();
      }

      auth.currentUser?.getIdTokenResult().then((idTokenResult) => {
        const roles = idTokenResult.claims.roles as string[] | undefined;

        const aliasAdmin = `${Utils.GetAlias()}-admin`;
        const isAdmin = roles?.includes("admin") || roles?.includes(aliasAdmin);

        setIsAdmin(!!isAdmin);
      });

      setUserLoaded(true);
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    updateCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    const siteKey = getSiteKey();
    const VISIT_FLAG = `orderq_visit_counted_this_tab_${siteKey ?? "default"}`;
    const alreadyCounted = sessionStorage.getItem(VISIT_FLAG);
    if (!alreadyCounted) {
      const now = new Date();
      const today = now.toISOString().slice(0, 10);
      const hour = now.getHours();
      sessionStorage.setItem(VISIT_FLAG, "1");
      (async () => {
        try {
          const visitsDoc = doc(db, "website", "visits");
          await setDoc(visitsDoc, {}, { merge: true });
          await updateDoc(visitsDoc, {
            totalVisits: increment(1),
            lastVisitAt: serverTimestamp(),
            [`daily.${today}`]: increment(1),
            [`hourly.${today}.${hour}`]: increment(1)
          });        
        } catch (error) {
          console.error("Error recording new visit:", error);
        }
      })();
    }
  }, []);

  const logOut = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setCart({ cartItems: [] });
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  return (
    <GlobalContext.Provider
      value={{
        cart,
        updateCart,
        settings,
        site,
        user,
        isAdmin,
        userloaded,
        settingsLoaded,
        logOut,
        scannedDeliveryLocation,
        setScannedDeliveryLocation
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};

export const useGlobal = () => {
  const context = useContext(GlobalContext);
  if (!context) {
    throw new Error("useGlobal must be used within a GlobalProvider");
  }
  return context;
};